/**
 * PD01 Thermal Printer Protocol Implementation
 *
 * Protocol format:
 * [0x51] [0x78] [CMD] [0x00] [LEN_LO] [LEN_HI] [DATA...] [CRC8] [0xFF]
 */

// Paper width: 384 dots (58mm at 200 DPI)
export const PRINTER_WIDTH = 384;
export const BYTES_PER_LINE = PRINTER_WIDTH / 8; // 48 bytes

// Command bytes (unsigned)
export const Commands = {
  GET_DEV_STATE: 0xA3,
  GET_DEV_INFO: 0xA8,
  SET_QUALITY: 0xA4,
  SET_ENERGY: 0xAF,
  APPLY_ENERGY: 0xBE,
  LATTICE_CTRL: 0xA6,
  PRINT_ROW: 0xA2,
  FEED_PAPER: 0xBD,
  SET_PAPER: 0xA1,
} as const;

// CRC8 lookup table
const CRC8_TABLE = new Uint8Array([
  0x00, 0x07, 0x0e, 0x09, 0x1c, 0x1b, 0x12, 0x15,
  0x38, 0x3f, 0x36, 0x31, 0x24, 0x23, 0x2a, 0x2d,
  0x70, 0x77, 0x7e, 0x79, 0x6c, 0x6b, 0x62, 0x65,
  0x48, 0x4f, 0x46, 0x41, 0x54, 0x53, 0x5a, 0x5d,
  0xe0, 0xe7, 0xee, 0xe9, 0xfc, 0xfb, 0xf2, 0xf5,
  0xd8, 0xdf, 0xd6, 0xd1, 0xc4, 0xc3, 0xca, 0xcd,
  0x90, 0x97, 0x9e, 0x99, 0x8c, 0x8b, 0x82, 0x85,
  0xa8, 0xaf, 0xa6, 0xa1, 0xb4, 0xb3, 0xba, 0xbd,
  0xc7, 0xc0, 0xc9, 0xce, 0xdb, 0xdc, 0xd5, 0xd2,
  0xff, 0xf8, 0xf1, 0xf6, 0xe3, 0xe4, 0xed, 0xea,
  0xb7, 0xb0, 0xb9, 0xbe, 0xab, 0xac, 0xa5, 0xa2,
  0x8f, 0x88, 0x81, 0x86, 0x93, 0x94, 0x9d, 0x9a,
  0x27, 0x20, 0x29, 0x2e, 0x3b, 0x3c, 0x35, 0x32,
  0x1f, 0x18, 0x11, 0x16, 0x03, 0x04, 0x0d, 0x0a,
  0x57, 0x50, 0x59, 0x5e, 0x4b, 0x4c, 0x45, 0x42,
  0x6f, 0x68, 0x61, 0x66, 0x73, 0x74, 0x7d, 0x7a,
  0x89, 0x8e, 0x87, 0x80, 0x95, 0x92, 0x9b, 0x9c,
  0xb1, 0xb6, 0xbf, 0xb8, 0xad, 0xaa, 0xa3, 0xa4,
  0xf9, 0xfe, 0xf7, 0xf0, 0xe5, 0xe2, 0xeb, 0xec,
  0xc1, 0xc6, 0xcf, 0xc8, 0xdd, 0xda, 0xd3, 0xd4,
  0x69, 0x6e, 0x67, 0x60, 0x75, 0x72, 0x7b, 0x7c,
  0x51, 0x56, 0x5f, 0x58, 0x4d, 0x4a, 0x43, 0x44,
  0x19, 0x1e, 0x17, 0x10, 0x05, 0x02, 0x0b, 0x0c,
  0x21, 0x26, 0x2f, 0x28, 0x3d, 0x3a, 0x33, 0x34,
  0x4e, 0x49, 0x40, 0x47, 0x52, 0x55, 0x5c, 0x5b,
  0x76, 0x71, 0x78, 0x7f, 0x6a, 0x6d, 0x64, 0x63,
  0x3e, 0x39, 0x30, 0x37, 0x22, 0x25, 0x2c, 0x2b,
  0x06, 0x01, 0x08, 0x0f, 0x1a, 0x1d, 0x14, 0x13,
  0xae, 0xa9, 0xa0, 0xa7, 0xb2, 0xb5, 0xbc, 0xbb,
  0x96, 0x91, 0x98, 0x9f, 0x8a, 0x8d, 0x84, 0x83,
  0xde, 0xd9, 0xd0, 0xd7, 0xc2, 0xc5, 0xcc, 0xcb,
  0xe6, 0xe1, 0xe8, 0xef, 0xfa, 0xfd, 0xf4, 0xf3,
]);

/**
 * Calculate CRC8 checksum for data
 */
export function calculateCRC8(data: Uint8Array, start: number = 0, length?: number): number {
  const len = length ?? data.length - start;
  let crc = 0;
  for (let i = start; i < start + len; i++) {
    crc = CRC8_TABLE[(crc ^ data[i]) & 0xff];
  }
  return crc;
}

/**
 * Build a command packet with header, data, CRC8, and terminator
 */
export function buildCommand(cmd: number, data: Uint8Array = new Uint8Array(0)): Uint8Array {
  const len = data.length;
  const packet = new Uint8Array(8 + len);

  // Header
  packet[0] = 0x51;
  packet[1] = 0x78;
  packet[2] = cmd;
  packet[3] = 0x00;

  // Length (16-bit little-endian)
  packet[4] = len & 0xff;
  packet[5] = (len >> 8) & 0xff;

  // Data
  packet.set(data, 6);

  // CRC8 over data portion
  packet[6 + len] = calculateCRC8(data);

  // Terminator
  packet[7 + len] = 0xff;

  return packet;
}

// Pre-built command packets

/**
 * Query device state
 */
export function cmdGetDevState(): Uint8Array {
  return buildCommand(Commands.GET_DEV_STATE, new Uint8Array([0x00]));
}

/**
 * Query device info (firmware version)
 */
export function cmdGetDevInfo(): Uint8Array {
  return buildCommand(Commands.GET_DEV_INFO, new Uint8Array([0x00]));
}

/**
 * Set quality to 200 DPI
 */
export function cmdSetQuality200DPI(): Uint8Array {
  return buildCommand(Commands.SET_QUALITY, new Uint8Array([0x32]));
}

/**
 * Set energy level (0x0000 - 0xFFFF)
 * Higher = darker print
 */
export function cmdSetEnergy(energy: number = 0xFFFF): Uint8Array {
  const high = (energy >> 8) & 0xff;
  const low = energy & 0xff;
  return buildCommand(Commands.SET_ENERGY, new Uint8Array([high, low]));
}

/**
 * Apply energy settings
 */
export function cmdApplyEnergy(): Uint8Array {
  return buildCommand(Commands.APPLY_ENERGY, new Uint8Array([0x01]));
}

/**
 * Lattice start - must be sent before image data
 */
export function cmdLatticeStart(): Uint8Array {
  const data = new Uint8Array([0xaa, 0x55, 0x17, 0x38, 0x44, 0x5f, 0x5f, 0x5f, 0x44, 0x38, 0x2c]);
  return buildCommand(Commands.LATTICE_CTRL, data);
}

/**
 * Lattice end - must be sent after image data
 */
export function cmdLatticeEnd(): Uint8Array {
  const data = new Uint8Array([0xaa, 0x55, 0x17, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x17]);
  return buildCommand(Commands.LATTICE_CTRL, data);
}

/**
 * Print a row of bitmap data
 * @param rowData - 48 bytes (384 pixels, 1 bit per pixel, LSB first)
 */
export function cmdPrintRow(rowData: Uint8Array): Uint8Array {
  if (rowData.length !== BYTES_PER_LINE) {
    throw new Error(`Row data must be ${BYTES_PER_LINE} bytes, got ${rowData.length}`);
  }
  return buildCommand(Commands.PRINT_ROW, rowData);
}

/**
 * Feed paper by specified number of lines
 */
export function cmdFeedPaper(lines: number = 25): Uint8Array {
  return buildCommand(Commands.FEED_PAPER, new Uint8Array([lines & 0xff]));
}

/**
 * Set paper command
 */
export function cmdSetPaper(): Uint8Array {
  return buildCommand(Commands.SET_PAPER, new Uint8Array([0x30, 0x00]));
}

/**
 * Convert image row to printer bitmap format
 * Each byte represents 8 horizontal pixels, LSB first
 * 1 = burn (black), 0 = no burn (white)
 */
export function imageToBitmap(imageData: ImageData): Uint8Array[] {
  const { width, height, data } = imageData;
  const rows: Uint8Array[] = [];

  for (let y = 0; y < height; y++) {
    const row = new Uint8Array(BYTES_PER_LINE);

    for (let byteIndex = 0; byteIndex < BYTES_PER_LINE; byteIndex++) {
      let byte = 0;

      for (let bit = 0; bit < 8; bit++) {
        const x = byteIndex * 8 + bit;

        if (x < width) {
          const pixelIndex = (y * width + x) * 4;
          const r = data[pixelIndex];
          const g = data[pixelIndex + 1];
          const b = data[pixelIndex + 2];
          const a = data[pixelIndex + 3];

          // Convert to grayscale and threshold
          // Treat transparent pixels as white
          const gray = a < 128 ? 255 : (r * 0.299 + g * 0.587 + b * 0.114);
          const isBlack = gray < 128;

          if (isBlack) {
            // LSB first: bit 0 = leftmost pixel
            byte |= (1 << bit);
          }
        }
      }

      row[byteIndex] = byte;
    }

    rows.push(row);
  }

  return rows;
}

/**
 * Parse device state response
 */
export function parseDeviceState(data: Uint8Array): { status: number; hasError: boolean } {
  // Response format: 5178a301 0300 0001 XXXX ff
  if (data.length >= 9 && data[0] === 0x51 && data[1] === 0x78 && data[2] === 0xa3) {
    const status = data[7];
    return {
      status,
      hasError: status !== 0x00
    };
  }
  return { status: 0, hasError: false };
}

/**
 * Check if response indicates print complete
 */
export function isPrintComplete(data: Uint8Array): boolean {
  // Response: 5178ae0101000000ff
  return data.length >= 9 &&
         data[0] === 0x51 &&
         data[1] === 0x78 &&
         data[2] === 0xae;
}

export type PrintProgress = {
  phase: 'init' | 'printing' | 'feeding' | 'finishing' | 'done' | 'error';
  progress: number; // 0-100
  currentRow: number;
  totalRows: number;
  message: string;
};
